/** Spec 036 P10: validation of the raw indicator-modal field values (handoff 4.5). */
import type { IndicatorParamDef, ParamValue } from './types'

export type RawParamValues = Record<string, string | boolean>

export interface ParsedParams {
  values: Record<string, ParamValue>
  errors: Record<string, string>
}

/**
 * Parses what the operator typed. `int`/`float` fields give `Enter a number`,
 * `Whole number only`, then `Must be above 0` - or, when the catalog declares
 * `min`/`max` (the hour fields), `{min}-{max}` for a value outside the range.
 * Bool and choice values pass through.
 */
export function parseIndicatorParams(defs: IndicatorParamDef[], raw: RawParamValues): ParsedParams {
  const values: Record<string, ParamValue> = {}
  const errors: Record<string, string> = {}
  for (const def of defs) {
    const v = raw[def.name]
    if (def.type === 'bool') {
      values[def.name] = v === true
    } else if (def.type === 'choice') {
      values[def.name] = String(v ?? def.default)
    } else {
      const s = String(v ?? '').trim()
      const n = Number(s)
      if (s === '' || !Number.isFinite(n)) errors[def.name] = 'Enter a number'
      else if (def.type === 'int' && !/^[-+]?\d+$/.test(s)) errors[def.name] = 'Whole number only'
      else if (def.min !== undefined || def.max !== undefined) {
        const lo = def.min ?? -Infinity
        const hi = def.max ?? Infinity
        if (n < lo || n > hi) errors[def.name] = `${def.min ?? ''}-${def.max ?? ''}`
        else values[def.name] = n
      } else if (n <= 0) errors[def.name] = 'Must be above 0'
      else values[def.name] = n
    }
  }
  return { values, errors }
}

/** The modal's raw string/bool form of a params object. */
export function toRawParams(values: Record<string, ParamValue>): RawParamValues {
  const out: RawParamValues = {}
  for (const [k, v] of Object.entries(values)) out[k] = typeof v === 'boolean' ? v : String(v)
  return out
}
